import { useEffect, useRef } from "react"
import { IntlShape } from "gatsby-plugin-intl"
import { AppState } from "../data/redux-state"
import checkSubVars from "../utils/checkSubVars"
import {
  CoVarOption,
  coVarKeyMainObservation,
  coVarKeyPrimaryTS,
} from "../utils/extractCoVariables"
import { extractDataLines, UncertaintyLine } from "../utils/extractLines"
import { getXRange, getYRange } from "./XYRange"

type ChartRanges = {
  dataXRange: [Date, Date]
  xRange: [Date, Date]
  dataYRange: [number, number]
  yRange: [number, number]
  aboveChartXrange: [Date, Date]
}

function areDateRangesEqual(
  left: [Date, Date] | null,
  right: [Date, Date] | null
): boolean {
  if (left === right) {
    return true
  }
  if (!left || !right) {
    return false
  }
  return (
    left[0].getTime() === right[0].getTime()
    && left[1].getTime() === right[1].getTime()
  )
}

function areNumberRangesEqual(
  left: [number, number] | null,
  right: [number, number] | null
): boolean {
  if (left === right) {
    return true
  }
  if (!left || !right) {
    return false
  }
  return left[0] === right[0] && left[1] === right[1]
}

function areChartRangesEqual(
  left: ChartRanges | null,
  right: ChartRanges | null
): boolean {
  if (left === right) {
    return true
  }
  if (!left || !right) {
    return false
  }
  return (
    areDateRangesEqual(left.dataXRange, right.dataXRange)
    && areDateRangesEqual(left.xRange, right.xRange)
    && areNumberRangesEqual(left.dataYRange, right.dataYRange)
    && areNumberRangesEqual(left.yRange, right.yRange)
    && areDateRangesEqual(left.aboveChartXrange, right.aboveChartXrange)
  )
}

function getStableChartRanges(
  ref: React.MutableRefObject<ChartRanges | null>,
  nextRanges: ChartRanges | null
): ChartRanges | null {
  if (areChartRangesEqual(ref.current, nextRanges)) {
    return ref.current
  }

  ref.current = nextRanges
  return nextRanges
}

export type ChartDisplayState = {
  hasData: boolean
  displayDataXRange: [Date, Date] | null
  displayXRange: [Date, Date] | null
  displayDataYRange: [number, number] | null
  displayYRange: [number, number] | null
  displayAboveChartXrange: [Date, Date] | null
  emptyChartMessage: string
}

function applySelectedXRange(
  range: [Date, Date],
  selectedXRange: [Date, Date] | null
): [Date, Date] {
  const nextRange: [Date, Date] = [new Date(range[0]), new Date(range[1])]
  if (!selectedXRange) {
    return nextRange
  }
  if (selectedXRange[0].getTime() > nextRange[0].getTime()) {
    nextRange[0] = selectedXRange[0]
  }
  if (selectedXRange[1].getTime() < nextRange[1].getTime()) {
    nextRange[1] = selectedXRange[1]
  }
  return nextRange
}

function getChartRanges(
  appState: AppState,
  data: UncertaintyLine[]
): ChartRanges | null {
  if (data.length === 0) {
    return null
  }

  const dataXRange = getXRange({
    data,
    extendToFullDecades: true,
  })
  const xRange = appState.xRange ?? dataXRange
  const dataYRange = getYRange(data, appState)
  const yRange = appState.yRange ?? dataYRange
  const aboveChartXrange = applySelectedXRange(
    getXRange({
      data,
      extendToFullDecades: false,
      aboveChart: true,
    }),
    appState.xRange
  )

  return {
    dataXRange,
    xRange,
    dataYRange,
    yRange,
    aboveChartXrange,
  }
}

function createDefaultCoVarOption(appState: AppState): CoVarOption {
  const defaultKey = appState.indicatorClass.startsWith("EWM")
    ? coVarKeyPrimaryTS
    : coVarKeyMainObservation
  return {
    value: defaultKey,
    name: defaultKey,
    sdev: false,
    ur: false,
    sdevEnabled: false,
    urEnabled: false,
    hasData: true,
  }
}

function getFallbackSourceData(appState: AppState): UncertaintyLine[] {
  if (appState.geoRegion.length > 0 && appState.coVarOptions.length > 0) {
    return []
  }

  const sourceJson = appState.sourceJson
  if (!sourceJson) {
    return []
  }

  const subVars = checkSubVars(sourceJson)
  const subVarKey = appState.subVariable.find(Boolean) ?? Object.keys(subVars)[0]
  if (!subVarKey || !subVars[subVarKey]) {
    return []
  }

  const geoRegions = Object.keys(subVars[subVarKey].GeoRegions ?? {})
  const fallbackRegion =
    appState.geoRegion.length > 0 ? appState.geoRegion : geoRegions.slice(0, 1)
  if (fallbackRegion.length === 0) {
    return []
  }

  const fallbackCoVars =
    appState.coVarOptions.length > 0
      ? appState.coVarOptions
      : [createDefaultCoVarOption(appState)]

  return extractDataLines({
    ...appState,
    geoRegion: fallbackRegion,
    coVarOptions: fallbackCoVars,
  }).data
}

function getEmptyChartMessage(appState: AppState, intl: IntlShape): string {
  const emptyChartMessageId =
    appState.geoRegion.length === 0 ||
    appState.subVariable.length === 0 ||
    appState.coVarOptions.length === 0
      ? "chart.noSelection"
      : "chart.noData"
  const defaultMessage =
    emptyChartMessageId === "chart.noSelection"
      ? "No data selected."
      : "No data available for this (Sub)Variable and Region."

  const translatedMessage = intl.messages[emptyChartMessageId]
  return typeof translatedMessage === "string"
    ? translatedMessage
    : defaultMessage
}

export function useChartDisplayState(args: {
  appState: AppState
  data: UncertaintyLine[]
  intl: IntlShape
}): ChartDisplayState {
  const lastNonEmptyRangesRef = useRef<ChartRanges | null>(null)
  const stableDisplayRangesRef = useRef<ChartRanges | null>(null)
  const hasData = args.data.length > 0
  const liveRanges = getChartRanges(args.appState, args.data)
  const fallbackSourceRanges = getChartRanges(
    args.appState,
    getFallbackSourceData(args.appState)
  )
  const displayRanges = getStableChartRanges(
    stableDisplayRangesRef,
    liveRanges ?? lastNonEmptyRangesRef.current ?? fallbackSourceRanges
  )

  useEffect(() => {
    if (liveRanges && !areChartRangesEqual(lastNonEmptyRangesRef.current, liveRanges)) {
      lastNonEmptyRangesRef.current = liveRanges
    }
  }, [liveRanges])

  return {
    hasData,
    displayDataXRange: displayRanges?.dataXRange ?? null,
    displayXRange: displayRanges?.xRange ?? null,
    displayDataYRange: displayRanges?.dataYRange ?? null,
    displayYRange: displayRanges?.yRange ?? null,
    displayAboveChartXrange: displayRanges?.aboveChartXrange ?? null,
    emptyChartMessage: getEmptyChartMessage(args.appState, args.intl),
  }
}